import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Plus, Sparkles, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';

interface SynchronicityLog {
  id: string;
  date: string;
  created_at: string;
  title: string;
  description: string;
  category: string;
  signs: string[];
  reflection?: string;
  pattern_connections?: string;
}

export default function SynchronicityTracker() {
  const { goBack, getBackButtonLabel } = useNavigationHistory();
  const [logs, setLogs] = useState<SynchronicityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    signs: [] as string[],
    reflection: '',
    pattern_connections: ''
  });

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('synchronicity_logs')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading synchronicities', variant: 'destructive' });
    } else {
      setLogs(data || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('synchronicity_logs')
      .insert({
        user_id: user.id,
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving synchronicity', variant: 'destructive' });
    } else {
      toast({ title: 'Synchronicity logged' });
      setFormData({
        title: '',
        description: '',
        category: '',
        signs: [],
        reflection: '',
        pattern_connections: ''
      });
      setShowForm(false);
      loadLogs();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading synchronicities...</div>;
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      <Button 
        onClick={goBack}
        variant="ghost" 
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {getBackButtonLabel()}
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Synchronicity Tracker
          </CardTitle>
          <CardDescription>
            Log meaningful coincidences, signs from the universe, and patterns that reveal deeper connections.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log Synchronicity
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-4 mb-6 p-4 border border-border rounded-lg bg-card">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Brief title for this synchronicity"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="What kind of sign?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="numbers">Numbers (111, 222, etc.)</SelectItem>
                    <SelectItem value="animals">Animal Messages</SelectItem>
                    <SelectItem value="songs">Songs/Music</SelectItem>
                    <SelectItem value="encounters">Chance Encounters</SelectItem>
                    <SelectItem value="timing">Divine Timing</SelectItem>
                    <SelectItem value="dreams">Dream Connections</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>What happened?</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the synchronicity in detail..."
                  rows={4}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Your Reflection</Label>
                <Textarea
                  value={formData.reflection}
                  onChange={(e) => setFormData({ ...formData, reflection: e.target.value })}
                  placeholder="What does this mean to you? What message are you receiving?"
                  rows={3}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Synchronicity</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start tracking synchronicities to see patterns and messages from the universe.
              </p>
            ) : (
              logs.map((log) => (
                <Card key={log.id} className="bg-muted/30">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <CardTitle className="text-lg">{log.title}</CardTitle>
                        <Badge variant="outline">{log.category}</Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(log.date), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <p className="text-sm">{log.description}</p>
                    {log.reflection && (
                      <div className="pt-2 border-t border-border/50">
                        <p className="text-sm font-light text-primary mb-1">Reflection:</p>
                        <p className="text-sm text-muted-foreground font-light italic">{log.reflection}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
